/**
 * @fileoverview Candidacy (Rental Application) Entity Configuration
 * 
 * Complete configuration for rental candidacies compatible with UniversalDetailPage.
 * Defines all fields, API endpoints, and custom behaviors for candidacy management.
 * 
 * @module Candidacy/EntityConfig
 */

import { UniversalEntityConfig } from 'shared/types/universalEntityConfig';
import { 
  User,
  EnvelopeSimple,
  Phone,
  Globe,
  MapPin,
  CalendarDot,
  Note,
  At,
  CalendarDotIcon
} from '@phosphor-icons/react';
import { API_URL } from 'constants/index';
import { format } from 'date-fns';

/**
 * Candidacy Entity Configuration
 */
export const CANDIDACY_ENTITY_CONFIG: UniversalEntityConfig = {
  // ============ Basic Info ============
  entityName: 'candidacy',
  displayName: 'Kandidatuur',
  displayNamePlural: 'Kandidaturen',
  entityPrefix: 'CAND-',
  
  // ============ API Endpoints ============
  api: {
    fetchOne: (id) => `${API_URL}/rental-appointment/${id}`,
    fetchAll: () => `${API_URL}/rental-appointment/get-appointments`,
    update: (id) => `${API_URL}/rental-appointment/update/${id}`,
    delete: (id) => `${API_URL}/rental-appointment/${id}`,
  },
  
  // ============ Title Field ============
  titleField: (candidacy) => {
    if (candidacy?.personalDetails) {
      return `${candidacy.personalDetails.firstName} ${candidacy.personalDetails.lastName}`;
    }
    return 'Kandidaat';
  },
  
  // ============ Fields ============
  fields: [
    // First Name
    {
      id: 'firstName',
      label: 'Voornaam',
      type: 'text',
      icon: User,
      tableConfig: {
        displayInTable: true,
        sortable: true,
        filterEnabled: true
      },
      detailConfig: {
        editable: false,
        section: 'main',
        required: true
      },
      getValue: (candidacy) => candidacy.personalDetails?.firstName,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ personalDetails: { firstName: value } })
    },
    
    // Last Name
    {
      id: 'lastName',
      label: 'Achternaam',
      type: 'text',
      icon: User,
      tableConfig: {
        displayInTable: true,
        sortable: true,
        filterEnabled: true
      },
      detailConfig: {
        editable: false,
        section: 'main',
        required: true
      },
      getValue: (candidacy) => candidacy.personalDetails?.lastName,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ personalDetails: { lastName: value } })
    },
    
    // Email
    {
      id: 'email',
      label: 'E-mailadres',
      type: 'text',
      icon: EnvelopeSimple,
      tableConfig: {
        displayInTable: true,
        sortable: true,
        filterEnabled: true
      },
      detailConfig: {
        editable: false,
        section: 'main'
      },
      getValue: (candidacy) => candidacy.personalDetails?.email,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ personalDetails: { email: value } })
    },
    
    // Phone Number
    {
      id: 'phoneNumber',
      label: 'Telefoonnummer',
      type: 'text',
      icon: Phone,
      tableConfig: {
        displayInTable: true,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'main'
      },
      getValue: (candidacy) => candidacy.personalDetails?.phoneNumber,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ personalDetails: { phoneNumber: value } })
    },
    
    // Spoken Languages
    {
      id: 'spokenLanguages',
      label: 'Aanspreektaal',
      type: 'text',
      icon: Globe,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'main'
      },
      getValue: (candidacy) => candidacy.personalDetails?.spokenLanguages,
      formatDisplay: (value) => Array.isArray(value) ? value.join(', ') : (value || '-'),
      buildUpdatePayload: (value) => ({ personalDetails: { spokenLanguages: value } })
    },
    
    // Nationality
    {
      id: 'nationality',
      label: 'Nationaliteit',
      type: 'text',
      icon: Globe,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'main'
      },
      getValue: (candidacy) => candidacy.personalDetails?.nationality,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ personalDetails: { nationality: value } })
    },
    
    // Address
    {
      id: 'address',
      label: 'Adres',
      type: 'text',
      icon: MapPin,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'main'
      },
      getValue: (candidacy) => candidacy.personalDetails?.address,
      formatDisplay: (value) => value || 'Geen adres opgegeven',
      buildUpdatePayload: (value) => ({ personalDetails: { address: value } })
    },
    
    // Move Date Preference
    {
      id: 'moveDate',
      label: 'Gewenste verhuisdatum',
      type: 'text',
      icon: CalendarDotIcon,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'metadata'
      },
      getValue: (candidacy) => candidacy.moveDate,
      formatDisplay: (value) => {
        if (!value) return 'Niet opgegeven';
        if (value.agreement) return 'Vanaf Unit beschikbaar';
        if (value.explanation) {
          try {
            return format(new Date(value.explanation), 'dd/MM/yyyy');
          } catch {
            return value.explanation;
          }
        }
        return 'Niet opgegeven';
      },
      buildUpdatePayload: (value) => ({ moveDate: value })
    },
    
    // Reference
    {
      id: 'referenceEmail',
      label: 'Referentie',
      type: 'text',
      icon: At,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'metadata'
      },
      getValue: (candidacy) => candidacy.referenceEmail,
      formatDisplay: (value) => value || 'Geen referentie opgegeven',
      buildUpdatePayload: (value) => ({ referenceEmail: value })
    },
    
    // Candidacy Date
    {
      id: 'createDate',
      label: 'Datum kandidatuur',
      type: 'text',
      icon: CalendarDot,
      tableConfig: {
        displayInTable: true,
        sortable: true,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'metadata'
      },
      getValue: (candidacy) => candidacy.createDate,
      formatDisplay: (value) => {
        if (!value) return '-';
        try {
          return format(new Date(value), 'dd/MM/yyyy');
        } catch {
          return value;
        }
      },
      buildUpdatePayload: (value) => ({ createDate: value })
    },
    
    // Extra Info
    {
      id: 'freeTextField',
      label: 'Extra informatie',
      type: 'textarea',
      icon: Note,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'metadata'
      },
      getValue: (candidacy) => candidacy.freeTextField,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ freeTextField: value })
    },
    
    // General Remarks
    {
      id: 'generalRemarks',
      label: 'Algemene opmerkingen (per unit)',
      type: 'textarea',
      icon: Note,
      tableConfig: {
        displayInTable: false,
        sortable: false,
        filterEnabled: false
      },
      detailConfig: {
        editable: false,
        section: 'metadata'
      },
      getValue: (candidacy) => candidacy.generalRemarks,
      formatDisplay: (value) => value || '-',
      buildUpdatePayload: (value) => ({ generalRemarks: value })
    },
  ],
  
  // ============ Metadata Chips ============
  getMetadataChips: (candidacy) => {
    const chips = [];
    
    // Status chip (first, for prominence)
    if (candidacy?.status) {
      // Status info with clear action responsibility colors
      const getStatusInfo = (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          // ✅ Positive completion (Green)
          'approved': { label: 'Goedgekeurd', color: 'border-green-500 bg-green-50 text-green-700' },
          'tenant': { label: 'Huurder', color: 'border-green-500 bg-green-50 text-green-700' },
          
          // ❌ Negative completion (Red)
          'rejected': { label: 'Afgewezen', color: 'border-red-500 bg-red-50 text-red-700' },
          'not-interested': { label: 'Geen interesse', color: 'border-red-500 bg-red-50 text-red-700' },
          
          // 🔵 They need to act (Blue)
          'awaiting-visit': { label: 'Bezoek ingepland', color: 'border-blue-500 bg-blue-50 text-blue-700' },
          'awaiting-interested': { label: 'Wacht op reactie', color: 'border-blue-500 bg-blue-50 text-blue-700' },
          'awaiting-alternate-selection': { label: 'Datum keuze gevraagd', color: 'border-blue-500 bg-blue-50 text-blue-700' },
          'invited': { label: 'Uitnodiging verstuurd', color: 'border-blue-500 bg-blue-50 text-blue-700' },
          
          // 🟠 We need to act (Orange)
          'interested': { label: 'Interesse bevestigd', color: 'border-orange-500 bg-orange-50 text-orange-700' },
          'notify': { label: 'Uitnodiging versturen', color: 'border-orange-500 bg-orange-50 text-orange-700' },
        };
        return statusMap[status] || { label: status, color: 'border-neutral-400 bg-neutral-50 text-neutral-700' };
      };
      
      const statusInfo = getStatusInfo(candidacy.status);
      chips.push({
        label: statusInfo.label,
        color: statusInfo.color,
      });
    }
    
    // Unit chip
    if (candidacy?.unitId) {
      chips.push({
        label: `Unit: ${candidacy.unitId}`,
        color: 'border-neutral-400 bg-neutral-50 text-neutral-700',
      });
    }
    
    // Number of residents chip
    const totalResidents = (candidacy?.numberOfAdults || 0) + (candidacy?.numberOfChildren || 0);
    if (totalResidents > 0) {
      chips.push({
        label: `${totalResidents} ${totalResidents === 1 ? 'bewoner' : 'bewoners'}`,
        color: 'border-moss-primary/30 bg-moss-primary/10 text-moss-primary',
      });
    }
    
    // Pets chip
    if (candidacy?.hasPets) {
      chips.push({
        label: 'Heeft huisdieren',
        color: 'border-blue-300 bg-blue-50 text-blue-700',
      });
    }
    
    // Smoking chip
    if (candidacy?.smoking) {
      chips.push({
        label: 'Roker',
        color: 'border-orange-300 bg-orange-50 text-orange-700',
      });
    }
    
    return chips;
  },
  
  // ============ Navigation ============
  navigation: {
    backRoute: '/rental/candidacies',
    enableInterPageNav: true,
    useFiltering: true,
    customFilterLogic: (allCandidacies, searchParams) => {
      // Apply same filters as on list page
      let filtered = [...allCandidacies];
      
      // Status filters
      const statusFilters = searchParams.get('statusArray')?.split(',').filter(Boolean);
      if (statusFilters && statusFilters.length > 0) {
        filtered = filtered.filter(c => statusFilters.includes(c.status));
      }
      
      // Search text filter
      const searchText = searchParams.get('searchText');
      if (searchText && searchText.trim()) {
        const term = searchText.trim().toLowerCase();
        filtered = filtered.filter(c => {
          const fullName = `${c.personalDetails?.firstName} ${c.personalDetails?.lastName}`.toLowerCase();
          const email = c.personalDetails?.email?.toLowerCase() || '';
          const unitId = c.unitId?.toLowerCase() || '';
          
          return fullName.includes(term) || email.includes(term) || unitId.includes(term);
        });
      }
      
      return filtered;
    },
    customNavigationHandler: (targetId, searchParams) => {
      // Preserve all URL params when navigating
      const params = new URLSearchParams(searchParams);
      window.location.href = `/rental/candidacies/${targetId}?${params.toString()}`;
    }
  },
  
  // ============ Display Configuration ============
  loadingText: 'Kandidaat laden...',
  notFoundText: 'Kandidaat niet gevonden',
  
  // ============ Documents & Emails ============
  documents: {
    enabled: false, // Candidacies don't have documents yet
  },
  
  emails: {
    enabled: false, // Handle emails via custom actions
  },
  
  // ============ Plugins & Customization ============
  plugins: {
    // Transform entity data after fetching
    transformEntityData: (data) => {
      // Convert date strings to Date objects
      if (data.createDate) {
        data.createDate = new Date(data.createDate);
      }
      if (data.visitDate) {
        data.visitDate = new Date(data.visitDate);
      }
      if (data.moveDate?.explanation) {
        data.moveDate.explanation = new Date(data.moveDate.explanation);
      }
      
      return data;
    },
    
    // Custom sections will be added in the detail page
    customSections: [],
    
    // Custom actions
    actions: [],
    
    // Hook called after entity is loaded
    onEntityLoad: async (entity, _context) => {
      console.log('✅ Candidacy loaded:', entity);
    },
    
    // Hook called after entity is updated
    onEntityUpdate: async (entity, _context) => {
      console.log('✅ Candidacy updated:', entity);
    },
  },
};

